// skipcq: JS-W1028
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

/** @typedef {import('./BrewhouseBreadcrumbs').BrewhouseBreadcrumbSegment} BrewhouseBreadcrumbSegment */

/**
 * @typedef {object} BrewhousePageHeaderState
 * @property {BrewhouseBreadcrumbSegment[]} breadcrumbs
 * @property {{ to: string, label: string } | null} backLink
 * @property {string | null} title
 * @property {string | null} titleTestId
 */

const EMPTY_HEADER = {
  breadcrumbs: [],
  backLink: null,
  title: null,
  titleTestId: null,
};

const BrewhousePageHeaderContext = createContext(null);

export function BrewhousePageHeaderProvider({ children }) {
  const [pageHeader, setPageHeaderState] = useState(EMPTY_HEADER);

  const setPageHeader = useCallback(function setPageHeader(nextHeader) {
    setPageHeaderState({
      breadcrumbs: Array.isArray(nextHeader?.breadcrumbs) ? nextHeader.breadcrumbs : [],
      backLink: nextHeader?.backLink ?? null,
      title: typeof nextHeader?.title === 'string' ? nextHeader.title : null,
      titleTestId: typeof nextHeader?.titleTestId === 'string' ? nextHeader.titleTestId : null,
    });
  }, []);

  const clearPageHeader = useCallback(function clearPageHeader() {
    setPageHeaderState(EMPTY_HEADER);
  }, []);

  const value = useMemo(
    () => ({ pageHeader, setPageHeader, clearPageHeader }),
    [pageHeader, setPageHeader, clearPageHeader],
  );

  return (
    <BrewhousePageHeaderContext.Provider value={value}>
      {children}
    </BrewhousePageHeaderContext.Provider>
  );
}

BrewhousePageHeaderProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Registers page-specific header content with the portal layout header.
 *
 * @param {BrewhousePageHeaderState} config
 */
export function useBrewhousePageHeaderRegistration(config) {
  const context = useContext(BrewhousePageHeaderContext);
  const setPageHeader = context?.setPageHeader;
  const clearPageHeader = context?.clearPageHeader;
  const breadcrumbsKey = JSON.stringify(config?.breadcrumbs ?? []);
  const backLinkKey = JSON.stringify(config?.backLink ?? null);

  useEffect(() => {
    if (!setPageHeader || !clearPageHeader) {
      return undefined;
    }

    setPageHeader(config);
    return clearPageHeader;
  }, [
    breadcrumbsKey,
    backLinkKey,
    config?.title,
    config?.titleTestId,
    setPageHeader,
    clearPageHeader,
  ]);
}

/**
 * @returns {BrewhousePageHeaderState}
 */
export function useBrewhousePageHeaderState() {
  const context = useContext(BrewhousePageHeaderContext);
  if (context === null) {
    return EMPTY_HEADER;
  }
  return context.pageHeader;
}

export default BrewhousePageHeaderContext;
